import { Suspense } from 'react';
import type { RouteObject } from 'react-router';
import { LoadingScreen } from '@/components/LoadingScreen/LoadingScreen';
import { ForgotPasswordForm } from '@/components/LoginForm/ForgotPasswordForm';
// Auth screens — small, keep static for instant render
import { LoginForm } from '@/components/LoginForm/LoginForm';
import { PreRegistration } from '@/components/RegistrationForm/PreRegistration';
import { RegistrationForm } from '@/components/RegistrationForm/RegistrationForm';
import { RegistrationSuccess } from '@/components/RegistrationForm/RegistrationSuccess';
import { ResetPasswordForm } from '@/components/ResetPasswordForm/ResetPasswordForm';
import { paths } from '@/constants';
import { Layout } from '@/screens/Layout';
import { LayoutSign } from '@/screens/LayoutSign';
import { NoMatch } from '@/screens/NoMatch';
import { VerifyEmail } from '@/screens/VerifyEmail/VerifyEmail';
import { lazyWithRetry as lazy } from '@/utils/lazyWithRetry';
import { RequireAuth } from '@/utils/RequireAuth';

// Lazy-loaded screens
const Company = lazy(() => import('@/screens/Company/Company').then(m => ({ default: m.Company })));
const Projects = lazy(() => import('@/screens/Projects/Projects').then(m => ({ default: m.Projects })));
const CreateCompany = lazy(() =>
    import('@/screens/CreateCompany/CreateCompany').then(m => ({
        default: m.CreateCompany,
    })),
);
const Employees = lazy(() =>
    import('@/screens/Employees/Employees').then(m => ({
        default: m.Employees,
    })),
);
const InviteEmployees = lazy(() =>
    import('@/screens/InviteEmployees/InviteEmployees').then(m => ({
        default: m.InviteEmployees,
    })),
);
const Project = lazy(() => import('@/screens/Project/Project').then(m => ({ default: m.Project })));
const Profile = lazy(() => import('@/screens/Profile/Profile').then(m => ({ default: m.Profile })));
const ProfileEdit = lazy(() =>
    import('@/screens/ProfileEdit/ProfileEdit').then(m => ({
        default: m.ProfileEdit,
    })),
);
const EpisodesSynopsis = lazy(() =>
    import('@/screens/EpisodesSynopsis/EpisodesSynopsis').then(m => ({
        default: m.EpisodesSynopsis,
    })),
);
const Editor = lazy(() => import('@/screens/Editor/Editor').then(m => ({ default: m.Editor })));
const EditorPep = lazy(() =>
    import('@/screens/EditorPep/EditorPep').then(m => ({
        default: m.EditorPep,
    })),
);
const EpisodeTitleList = lazy(() =>
    import('@/screens/EpisodeTitleList/EpisodeTitleList').then(m => ({
        default: m.EpisodeTitleList,
    })),
);
const Line = lazy(() => import('@/screens/Line/Line').then(m => ({ default: m.Line })));
const Lines = lazy(() => import('@/screens/Lines/Lines').then(m => ({ default: m.Lines })));
const Plan = lazy(() => import('@/screens/Plan/Plan').then(m => ({ default: m.Plan })));
const Characters = lazy(() =>
    import('@/screens/Characters/Characters').then(m => ({
        default: m.Characters,
    })),
);
const Character = lazy(() =>
    import('@/screens/Character/Character').then(m => ({
        default: m.Character,
    })),
);
const Versions = lazy(() => import('@/screens/Versions/Versions').then(m => ({ default: m.Versions })));
const ImportReports = lazy(() => import('@/screens/ImportReports/ImportReports').then(m => ({ default: m.ImportReports })));
const Locations = lazy(() =>
    import('@/screens/Locations/Locations').then(m => ({
        default: m.Locations,
    })),
);
const Location = lazy(() => import('@/screens/Location/Location').then(m => ({ default: m.Location })));
const Feedback = lazy(() => import('@/screens/Feedback/Feedback').then(m => ({ default: m.Feedback })));
const Tutorial = lazy(() => import('@/screens/Tutorial/Tutorial').then(m => ({ default: m.Tutorial })));
const Start = lazy(() => import('@/screens/Start/Start').then(m => ({ default: m.Start })));
const FountainDemoSynopsis = lazy(() =>
    import('@/screens/FountainDemo/FountainDemoSynopsis').then(m => ({
        default: m.FountainDemoSynopsis,
    })),
);
const FountainDemoSetting = lazy(() =>
    import('@/screens/FountainDemo/FountainDemoSetting').then(m => ({
        default: m.FountainDemoSetting,
    })),
);
const FountainDemoTheme = lazy(() =>
    import('@/screens/FountainDemo/FountainDemoTheme').then(m => ({
        default: m.FountainDemoTheme,
    })),
);
const FountainDemoReferences = lazy(() =>
    import('@/screens/FountainDemo/FountainDemoReferences').then(m => ({
        default: m.FountainDemoReferences,
    })),
);
const FountainDemoMarketing = lazy(() =>
    import('@/screens/FountainDemo/FountainDemoMarketing').then(m => ({
        default: m.FountainDemoMarketing,
    })),
);
const FountainDemoDocuments = lazy(() =>
    import('@/screens/FountainDemo/FountainDemoDocuments').then(m => ({
        default: m.FountainDemoDocuments,
    })),
);
const DevToolsFillProject = lazy(() =>
    import('@/screens/DevTools/FillProject').then(m => ({
        default: m.FillProject,
    })),
);
const FountainHotkeySandbox = lazy(() =>
    import('@/screens/FountainHotkeySandbox/FountainHotkeySandbox').then(m => ({
        default: m.FountainHotkeySandbox,
    })),
);
const ConceptEditor = lazy(() =>
    import('@/screens/ConceptEditor/ConceptEditor').then(m => ({
        default: m.ConceptEditor,
    })),
);

const Lazy = ({ children }: { children: React.ReactNode }) => <Suspense fallback={<LoadingScreen />}>{children}</Suspense>;

export const routes: RouteObject[] = [
    {
        path: paths.profile.index,
        element: (
            <RequireAuth>
                <Layout />
            </RequireAuth>
        ),
        children: [
            {
                path: paths.company.index,
                element: (
                    <Lazy>
                        <Company />
                    </Lazy>
                ),
            },
            {
                path: paths.company.projects,
                element: (
                    <Lazy>
                        <Projects />
                    </Lazy>
                ),
            },
            {
                path: paths.company.projectsArchive,
                element: (
                    <Lazy>
                        <Projects mode="archive" />
                    </Lazy>
                ),
            },
            {
                path: paths.company.projectsDeleted,
                element: (
                    <Lazy>
                        <Projects mode="deleted" />
                    </Lazy>
                ),
            },
            {
                path: paths.company.create,
                element: (
                    <Lazy>
                        <CreateCompany />
                    </Lazy>
                ),
            },
            {
                path: paths.employees.index,
                element: (
                    <Lazy>
                        <Employees />
                    </Lazy>
                ),
            },
            {
                path: paths.employees.invite,
                element: (
                    <Lazy>
                        <InviteEmployees />
                    </Lazy>
                ),
            },
            {
                path: paths.project.index,
                element: (
                    <Lazy>
                        <Project />
                    </Lazy>
                ),
            },
            {
                path: paths.project.create,
                element: (
                    <Lazy>
                        <Project create />
                    </Lazy>
                ),
            },
            {
                path: paths.project.synopsis.index,
                element: (
                    <Lazy>
                        <FountainDemoSynopsis />
                    </Lazy>
                ),
            },
            {
                path: paths.project.synopsis.setting,
                element: (
                    <Lazy>
                        <FountainDemoSetting />
                    </Lazy>
                ),
            },
            {
                path: paths.project.synopsis.theme,
                element: (
                    <Lazy>
                        <FountainDemoTheme />
                    </Lazy>
                ),
            },
            {
                path: paths.project.synopsis.references,
                element: (
                    <Lazy>
                        <FountainDemoReferences />
                    </Lazy>
                ),
            },
            {
                path: paths.project.synopsis.marketing,
                element: (
                    <Lazy>
                        <FountainDemoMarketing />
                    </Lazy>
                ),
            },
            {
                path: paths.project.synopsis.documents,
                element: (
                    <Lazy>
                        <FountainDemoDocuments />
                    </Lazy>
                ),
            },
            {
                path: paths.project.episodes.synopsis,
                element: (
                    <Lazy>
                        <EpisodesSynopsis />
                    </Lazy>
                ),
            },
            {
                path: paths.project.plan.index,
                element: (
                    <Lazy>
                        <Plan />
                    </Lazy>
                ),
            },
            { path: paths.project.comments, element: <></> },
            {
                path: paths.project.line.index,
                element: (
                    <Lazy>
                        <Line />
                    </Lazy>
                ),
            },
            {
                path: paths.project.lines.index,
                element: (
                    <Lazy>
                        <Lines />
                    </Lazy>
                ),
            },
            {
                path: paths.project.scene.editor,
                element: (
                    <Lazy>
                        <Editor />
                    </Lazy>
                ),
            },
            {
                path: paths.project.scene.editor_pep,
                element: (
                    <Lazy>
                        <EditorPep />
                    </Lazy>
                ),
            },
            {
                path: paths.project.scene.title,
                element: (
                    <Lazy>
                        <EpisodeTitleList />
                    </Lazy>
                ),
            },
            {
                path: paths.project.versions,
                element: (
                    <Lazy>
                        <Versions />
                    </Lazy>
                ),
            },
            {
                path: paths.project.importReports,
                element: (
                    <Lazy>
                        <ImportReports />
                    </Lazy>
                ),
            },
            {
                path: paths.project.characters.index,
                element: (
                    <Lazy>
                        <Characters />
                    </Lazy>
                ),
            },
            {
                path: paths.project.character.index,
                element: (
                    <Lazy>
                        <Character />
                    </Lazy>
                ),
            },
            {
                path: paths.profile.index,
                element: (
                    <Lazy>
                        <Profile />
                    </Lazy>
                ),
            },
            {
                path: paths.profile.edit,
                element: (
                    <Lazy>
                        <ProfileEdit />
                    </Lazy>
                ),
            },
            {
                path: paths.project.locations.index,
                element: (
                    <Lazy>
                        <Locations />
                    </Lazy>
                ),
            },
            {
                path: paths.project.location.index,
                element: (
                    <Lazy>
                        <Location />
                    </Lazy>
                ),
            },
            {
                path: paths.feedback.index,
                element: (
                    <Lazy>
                        <Feedback />
                    </Lazy>
                ),
            },
            {
                path: paths.tutorial.index,
                element: (
                    <Lazy>
                        <Tutorial />
                    </Lazy>
                ),
            },
            {
                // Hidden devtools — not advertised in nav. Direct-URL access only.
                // Lives under /project/... to read the active project from the store
                // and to inherit the project-context sidebar in Navigation.
                path: paths.project.devtools.fillProject,
                element: (
                    <Lazy>
                        <DevToolsFillProject />
                    </Lazy>
                ),
            },
            { path: '*', element: <NoMatch /> },
        ],
    },
    {
        path: paths.sign.index,
        element: <LayoutSign />,
        children: [
            { index: true, element: <LoginForm /> },
            { path: paths.sign.verify, element: <VerifyEmail /> },
            { path: paths.sign.preRegistration, element: <PreRegistration /> },
            { path: paths.sign.registration, element: <RegistrationForm /> },
            {
                path: paths.sign.registrationSuccess,
                element: <RegistrationSuccess />,
            },
            { path: paths.sign.resetPassword, element: <ResetPasswordForm /> },
            { path: paths.sign.forgotPassword, element: <ForgotPasswordForm /> },
            { path: '*', element: <NoMatch /> },
        ],
    },
    {
        path: paths.start.index,
        element: (
            <Lazy>
                <Start />
            </Lazy>
        ),
    },
    {
        path: '/sandbox/fountain-hotkeys',
        element: (
            <Lazy>
                <FountainHotkeySandbox />
            </Lazy>
        ),
    },
    {
        element: <Layout />,
        children: [
            {
                path: paths.concept.editor,
                element: (
                    <Lazy>
                        <ConceptEditor />
                    </Lazy>
                ),
            },
        ],
    },
];
